import {
  assetsCollection,
  COLLECTION_COLORS,
  ideasCollection,
  mechanicsCollection,
  STARTER_SECTIONS,
  tasksCollection,
  uid,
  type ColorName,
  type NewCollection,
  type StarterCollection,
  type StarterSection,
} from '@tomo/core';
import { Button, Field, IconButton, Input, Select, Switch, Textarea, toast } from '@tomo/ui';
import { Cloud, HardDrive, Plus, X } from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';
import { ApiRequestError } from '../../data/cloud/api.ts';
import { useCloud } from '../../data/cloud/session.ts';
import { createProject } from '../../data/projects.ts';
import { IconPicker } from '../shell/IconPicker.tsx';
import { ProjectTile } from '../shell/ProjectTile.tsx';
import { canHoldProjects, moveProjectToWorkspace } from './project-actions.ts';
import './projects.css';

/** How a ready-made collection looks in the sidebar, taken from the preset that makes it. */
const look = ({ name, icon, color }: NewCollection) => ({ name, icon: icon!, color: color!, collection: true });

/** The ready-made parts a project can start with (see `applyTemplate`). */
const SECTIONS: Record<StarterSection, { name: string; icon: string; color: ColorName; hint: string; collection: boolean }> = {
  mechanics: { ...look(mechanicsCollection()), hint: 'Design docs, starting with a short guide.' },
  ideas: { ...look(ideasCollection()), hint: 'Ideas the team can vote on.' },
  tasks: { ...look(tasksCollection()), hint: 'A task board with assignees and due dates.' },
  assets: { ...look(assetsCollection()), hint: 'Art, audio and reference files.' },
  boards: { name: 'Board', icon: 'i:shapes', color: 'yellow', hint: 'Notes, sketches, shapes and images.', collection: false },
  votes: { name: 'Votes', icon: 'i:vote', color: 'purple', hint: 'Polls for decisions you make together.', collection: false },
};
/** The "Save to" choice that keeps the project on this device. */
const DEVICE = 'device';

type OwnCollection = Required<StarterCollection> & { key: string };

/** A new row takes the next color in turn, as in the New collection dialog. */
const newRow = (index: number): OwnCollection => ({ key: uid(), name: '', icon: 'i:box', color: COLLECTION_COLORS[index % COLLECTION_COLORS.length]! });

/**
 * For each row, the name it repeats (a ready-made collection that's switched on, or an earlier
 * row), if it does.
 */
function repeatedNames(rows: OwnCollection[], sections: StarterSection[]): (string | null)[] {
  const seen = new Set(sections.filter((s) => SECTIONS[s].collection).map((s) => SECTIONS[s].name.toLocaleLowerCase()));
  return rows.map((r) => {
    const name = r.name.trim();
    if (!name) return null;
    if (seen.has(name.toLocaleLowerCase())) return name;
    seen.add(name.toLocaleLowerCase());
    return null;
  });
}

/** State of the "new project" form, shared by the dialog and the create page. */
export function useNewProjectForm(onCreated: (projectId: string) => void) {
  const cloud = useCloud();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('i:sparkle');
  const [color, setColor] = useState<ColorName>('yellow');
  const [collections, setCollections] = useState(() => [newRow(0)]);
  const [sections, setSections] = useState<StarterSection[]>([...STARTER_SECTIONS]);
  const [target, setTarget] = useState(DEVICE);
  const [busy, setBusy] = useState(false);

  const workspaces = cloud.status === 'signed-in' ? cloud.workspaces.filter(canHoldProjects) : [];
  // If the chosen workspace goes away (signing out, a downgrade), the project stays on this device.
  const workspace = workspaces.find((w) => w.id === target) ?? null;

  /** Add an empty row and return its key. */
  const addCollection = () => {
    const row = newRow(collections.length);
    setCollections((list) => [...list, row]);
    return row.key;
  };
  const updateCollection = (key: string, patch: Partial<StarterCollection>) => setCollections((list) => list.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  const removeCollection = (key: string) => setCollections((list) => list.filter((c) => c.key !== key));
  // Kept in sidebar order.
  const toggleSection = (section: StarterSection) =>
    setSections((on) => (on.includes(section) ? on.filter((s) => s !== section) : STARTER_SECTIONS.filter((s) => s === section || on.includes(s))));

  const submit = async () => {
    const title = name.trim();
    if (!title || busy) return;
    setBusy(true);
    try {
      // Empty rows and names already in the project are left out.
      const repeated = repeatedNames(collections, sections);
      const own = collections.filter((c, i) => c.name.trim() && !repeated[i]);
      const id = await createProject({ name: title, description: description.trim(), template: 'blank', icon, color, collections: own, sections });
      if (workspace) {
        try {
          await moveProjectToWorkspace({ id, name: title, icon, color }, workspace);
        } catch (e) {
          toast.error('Saved on this device only', { description: `Could not add it to ${workspace.name}: ${e instanceof ApiRequestError ? e.message : String(e)}` });
        }
      }
      onCreated(id);
    } catch (e) {
      toast.error('Could not create the project', { description: String(e) });
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setName('');
    setDescription('');
    setIcon('i:sparkle');
    setColor('yellow');
    setCollections([newRow(0)]);
    setSections([...STARTER_SECTIONS]);
    setTarget(DEVICE);
  };

  return {
    name,
    setName,
    description,
    setDescription,
    icon,
    setIcon,
    color,
    setColor,
    collections,
    addCollection,
    updateCollection,
    removeCollection,
    sections,
    setSections,
    toggleSection,
    workspaces,
    workspace,
    setTarget,
    busy,
    submit,
    reset,
  };
}

export type NewProjectForm = ReturnType<typeof useNewProjectForm>;

/** Enter on its own, and not while an input method is composing text. */
const plainEnter = (e: KeyboardEvent) => e.key === 'Enter' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.nativeEvent.isComposing;

/**
 * Name, pitch, icon, the game's own collections, the ready-made parts to start with and where
 * to save it. Ctrl/⌘+Enter submits.
 */
export function NewProjectFields({ form }: { form: NewProjectForm }) {
  return (
    <form
      className="new-project"
      onSubmit={(e) => {
        e.preventDefault();
        void form.submit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void form.submit();
      }}
    >
      <IconPicker
        value={form.icon}
        onChange={(t) => t && form.setIcon(t)}
        color={form.color}
        onColorChange={form.setColor}
        trigger={
          <button type="button" className="new-project__icon" aria-label="Choose icon and color">
            <ProjectTile icon={form.icon} color={form.color} size={56} />
          </button>
        }
      />
      <div className="new-project__fields">
        <Field label="Name" htmlFor="np-name">
          <Input
            id="np-name"
            size="lg"
            autoFocus
            placeholder="e.g. Starfall Tactics"
            value={form.name}
            onChange={(e) => form.setName(e.target.value)}
            onKeyDown={(e) => {
              // The name is all a project needs, so Enter creates it.
              if (!plainEnter(e)) return;
              e.preventDefault();
              void form.submit();
            }}
          />
        </Field>
        <Field label="Pitch" htmlFor="np-desc" hint="Optional — one line about the game.">
          <Textarea
            id="np-desc"
            rows={2}
            autoGrow
            placeholder="A roguelike deck-builder where…"
            value={form.description}
            onChange={(e) => form.setDescription(e.target.value)}
          />
        </Field>
        <OwnCollections form={form} />
        <StarterSections form={form} />
        {form.workspaces.length > 0 && <SaveTo form={form} />}
      </div>
    </form>
  );
}

/** The things the game is made of. Each becomes an empty collection with a table. */
function OwnCollections({ form }: { form: NewProjectForm }) {
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const addButton = useRef<HTMLButtonElement>(null);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const repeated = repeatedNames(form.collections, form.sections);
  const firstRepeat = repeated.find(Boolean);
  const readyMade = firstRepeat && form.sections.find((s) => SECTIONS[s].collection && SECTIONS[s].name.toLocaleLowerCase() === firstRepeat.toLocaleLowerCase());

  const add = () => setFocusKey(form.addCollection());
  // Enter moves to the next row, and after a named last row, adds one.
  const next = (index: number) => {
    const after = form.collections[index + 1];
    if (after) inputs.current.get(after.key)?.focus();
    else if (form.collections[index]!.name.trim()) add();
  };
  const remove = (index: number) => {
    const neighbor = form.collections[index - 1] ?? form.collections[index + 1];
    form.removeCollection(form.collections[index]!.key);
    (neighbor ? inputs.current.get(neighbor.key) : addButton.current)?.focus();
  };

  return (
    <div className="g-field" role="group" aria-labelledby="np-collections">
      <div className="g-field__top">
        <span id="np-collections" className="g-field__label">
          Collections
        </span>
        <span className="g-field__aside">Optional</span>
      </div>
      {form.collections.length > 0 && (
        <div className="own-collections">
          {form.collections.map((c, i) => (
            <div key={c.key} className="own-collection">
              <IconPicker
                value={c.icon}
                onChange={(t) => t && form.updateCollection(c.key, { icon: t })}
                color={c.color}
                onColorChange={(color) => form.updateCollection(c.key, { color })}
                trigger={
                  <button type="button" className="own-collection__tile" aria-label={`Icon and color of ${c.name.trim() || `collection ${i + 1}`}`}>
                    <ProjectTile icon={c.icon} color={c.color} size={32} />
                  </button>
                }
              />
              <Input
                ref={(el) => {
                  if (el) inputs.current.set(c.key, el);
                  else inputs.current.delete(c.key);
                }}
                aria-label={`Collection ${i + 1}`}
                placeholder={i === 0 ? 'e.g. Units' : 'Another collection'}
                value={c.name}
                invalid={!!repeated[i]}
                autoFocus={c.key === focusKey}
                wrapperClassName="own-collection__name"
                onChange={(e) => form.updateCollection(c.key, { name: e.target.value })}
                onKeyDown={(e) => {
                  if (!plainEnter(e)) return;
                  e.preventDefault();
                  next(i);
                }}
              />
              <IconButton label={`Remove ${c.name.trim() || `collection ${i + 1}`}`} size="sm" onClick={() => remove(i)}>
                <X />
              </IconButton>
            </div>
          ))}
        </div>
      )}
      <Button ref={addButton} variant="ghost" size="sm" icon={<Plus />} className="own-collections__add" onClick={add}>
        Add collection
      </Button>
      {readyMade ? (
        <p className="g-field__error">{SECTIONS[readyMade].name} is switched on below. Turn it off to make your own.</p>
      ) : firstRepeat ? (
        <p className="g-field__error">“{firstRepeat}” is already on the list.</p>
      ) : (
        <p className="g-field__hint">What your game is made of. Each starts empty, with a table.</p>
      )}
    </div>
  );
}

/** The ready-made parts, each with a switch. They all start on. */
function StarterSections({ form }: { form: NewProjectForm }) {
  const allOff = form.sections.length === 0;
  return (
    <div className="g-field" role="group" aria-labelledby="np-sections">
      <div className="g-field__top">
        <span id="np-sections" className="g-field__label">
          Also include
        </span>
        <button type="button" className="g-field__aside inline-link" onClick={() => form.setSections(allOff ? [...STARTER_SECTIONS] : [])}>
          {allOff ? 'Turn all on' : 'Turn all off'}
        </button>
      </div>
      <div className="starter-sections">
        {STARTER_SECTIONS.map((id) => {
          const s = SECTIONS[id];
          const on = form.sections.includes(id);
          return (
            <label key={id} className="starter-section" data-off={on ? undefined : ''}>
              <ProjectTile icon={s.icon} color={s.color} size={32} />
              <span className="starter-section__text">
                <span className="starter-section__name">{s.name}</span>
                <span id={`np-section-${id}`} className="starter-section__hint">
                  {s.hint}
                </span>
              </span>
              <Switch size="sm" checked={on} onCheckedChange={() => form.toggleSection(id)} aria-label={s.name} aria-describedby={`np-section-${id}`} />
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Where the project lives: this device, or a Pro workspace it syncs with. */
function SaveTo({ form }: { form: NewProjectForm }) {
  const ws = form.workspace;
  return (
    <Field
      label="Save to"
      htmlFor="np-save"
      hint={ws ? `Everyone in ${ws.name} can open it, and it still works offline.` : 'Saved in this browser, and works offline. You can move it to a workspace later.'}
    >
      <Select
        id="np-save"
        value={ws?.id ?? DEVICE}
        onChange={form.setTarget}
        options={[{ value: DEVICE, label: 'This device', icon: <HardDrive /> }, ...form.workspaces.map((w) => ({ value: w.id, label: w.name, icon: <Cloud /> }))]}
      />
    </Field>
  );
}
